/**
 * PepSettingsPage — Compliance & Capture · PEP Settings.
 *
 * Single-record settings FORM for the admin-configurable Politically Exposed
 * Person (PEP) control (Bill 2026-06-17). Surfaces the four `pep.*`
 * SystemSettings rows the FSR Verify-section checkbox + "More info" helper read:
 *   - pep.enabled         (master switch — hides the FSR checkbox + inerts the gate when off)
 *   - pep.helperText      (the "More info" wording the account-opening officer sees)
 *   - pep.enforcementMode (notify | deferred | block — how a PEP designation gates submission)
 *   - pep.deferredDays    (SLA days for the 'deferred' mode BSA review task)
 *
 * Read:  client.retail.config.pep() — GET /v3/config/pep (assembles + defaults the keys).
 * Write: client.retail.admin.systemSettings.upsert(SENTINEL, row) per changed key
 *        (the typed Wave-6.1 surface; sparse settingValue-only writes), categoryCode 'PEP'.
 *
 * Cloned from LexisNexisConfigPage (load via useEffect, dirty-tracking, Save
 * with toast/Banner, sentinel auth). Auth: ADMIN_AUTH_TOKEN_SENTINEL (MSAL
 * bearer carries authority; no secret in the bundle).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ADMIN_AUTH_TOKEN_SENTINEL,
  ApiError,
  type SystemSettingUpsertBody,
} from '@retail-ops/api-client';
import type { PepEnforcementMode } from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Spinner } from '@/components/primitives';
import { TextArea, SelectField, NumberField, BoolField } from '@/components/FormField';

interface FormState {
  enabled: boolean;
  helperText: string;
  enforcementMode: PepEnforcementMode;
  deferredDays: number | null;
}

const KEYS = {
  enabled: 'pep.enabled',
  helperText: 'pep.helperText',
  enforcementMode: 'pep.enforcementMode',
  deferredDays: 'pep.deferredDays',
} as const;

const EMPTY: FormState = {
  enabled: false,
  helperText: '',
  enforcementMode: 'notify',
  deferredDays: 30, // canonical default — matches seed 62 / GET /v3/config/pep / CartCommitService
};

const ENFORCEMENT_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'notify', label: 'Notify only — soft warn, does not delay opening (recommended)' },
  { value: 'deferred', label: 'Deferred review — open + N-day BSA task' },
  { value: 'block', label: 'Require BSA review before opening' },
];

export function PepSettingsPage(): JSX.Element {
  const client = useApiClient();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [original, setOriginal] = useState<FormState>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const res = await client.retail.config.pep();
        if (cancelled) return;
        if (!res.success || !res.data) {
          throw new Error(res.error ?? 'Could not load PEP config.');
        }
        const next: FormState = {
          enabled: res.data.enabled,
          helperText: res.data.helperText ?? '',
          enforcementMode: res.data.enforcementMode,
          deferredDays: res.data.deferredDays,
        };
        setForm(next);
        setOriginal(next);
      } catch (e) {
        if (cancelled) return;
        setLoadError(
          ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, refreshNonce]);

  const isDirty = useMemo(
    () =>
      form.enabled !== original.enabled ||
      form.helperText !== original.helperText ||
      form.enforcementMode !== original.enforcementMode ||
      form.deferredDays !== original.deferredDays,
    [form, original],
  );

  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const rows: SystemSettingUpsertBody[] = [];
      if (form.enabled !== original.enabled) {
        rows.push({
          settingKey: KEYS.enabled,
          settingValue: form.enabled ? 'true' : 'false',
          dataType: 'bool',
          categoryCode: 'PEP',
          isActive: true,
        });
      }
      if (form.helperText !== original.helperText) {
        rows.push({
          settingKey: KEYS.helperText,
          settingValue: form.helperText,
          dataType: 'string',
          categoryCode: 'PEP',
          isActive: true,
        });
      }
      if (form.enforcementMode !== original.enforcementMode) {
        rows.push({
          settingKey: KEYS.enforcementMode,
          settingValue: form.enforcementMode,
          dataType: 'string',
          categoryCode: 'PEP',
          isActive: true,
        });
      }
      if (form.deferredDays !== original.deferredDays) {
        rows.push({
          settingKey: KEYS.deferredDays,
          settingValue: String(form.deferredDays ?? EMPTY.deferredDays),
          dataType: 'int',
          categoryCode: 'PEP',
          isActive: true,
        });
      }

      for (const row of rows) {
        const resp = await client.retail.admin.systemSettings.upsert(ADMIN_AUTH_TOKEN_SENTINEL, row);
        if (!resp.success) throw new Error(resp.error ?? `upsert failed for ${row.settingKey}`);
      }
      setOriginal(form);
      setToast({
        tone: 'info',
        text: `Saved ${rows.length} setting${rows.length === 1 ? '' : 's'}.`,
      });
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [client, form, original]);

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load PEP settings: {loadError}{' '}
        <Button onClick={() => setRefreshNonce((n) => n + 1)}>Retry</Button>
      </Banner>
    );
  }
  if (loading) return <Spinner label="Loading PEP settings…" />;

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Banner tone="info">
        <strong>Politically Exposed Person (PEP) control.</strong> The FSR marks a customer as a PEP
        in the account-opening Verify section. These settings decide whether that checkbox appears at
        all, the wording behind its "More info" helper, and how a PEP designation gates the opening —
        soft warn, deferred BSA task, or a hard review-before-open block.
      </Banner>

      <Card title="Control">
        <div className="form-stack">
          <BoolField
            label="PEP control enabled"
            value={form.enabled}
            onChange={(v) => setForm({ ...form, enabled: v })}
            hint="When off, the FSR PEP checkbox is hidden and the gate is inert."
          />
        </div>
      </Card>

      <Card title="FSR-facing copy">
        <div className="form-stack">
          <TextArea
            label="FSR help text (More info)"
            value={form.helperText}
            onChange={(v) => setForm({ ...form, helperText: v })}
            rows={6}
            hint="The wording the account-opening officer sees behind the PEP checkbox's “More info”."
          />
        </div>
      </Card>

      <Card title="Enforcement">
        <div className="form-stack">
          <SelectField
            label="Review enforcement"
            value={form.enforcementMode}
            onChange={(v) => setForm({ ...form, enforcementMode: v as PepEnforcementMode })}
            options={ENFORCEMENT_OPTIONS}
            hint="How a PEP designation gates account submission."
          />
          <NumberField
            label="Deferred review days"
            value={form.deferredDays}
            onChange={(v) => setForm({ ...form, deferredDays: v })}
            min={1}
            max={90}
            hint="Only used by Deferred mode — the SLA for the N-day BSA review task."
          />
        </div>
      </Card>

      {saveError && <Banner tone="err">{saveError}</Banner>}

      <Card title="">
        <div className="card-actions-row">
          <Button variant="primary" onClick={() => void handleSave()} disabled={saving || !isDirty}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
          {isDirty && <span className="cell-desc">Unsaved changes.</span>}
        </div>
      </Card>
    </>
  );
}
